import { api, throwApiError } from "../../../api/api";
import type {
  DpPayrollMonthInputContext,
  DpPayrollPeriodStatus,
  DpPayrollResultLine,
  DpPayrollResultMeta,
  DpPayrollResultStatus,
  PayrollMonthInputPayload,
} from "./payrollApi";
import type { DpNoticeType, DpTerminationType } from "./processesApi";
import type { DpFinanceView } from "./financeBridge";

/** Apuração do mês do afastamento gravada no demonstrativo (sem espelho de ponto fechado). */
export type DpTerminationManualInput = {
  workedDays: number | null;
  absenceDays: number | null;
  dsrLostCount: number | null;
  overtimeWorkdayHours: string | null;
  overtimeRestDayHours: string | null;
  nightHours: string | null;
  lateMinutes: number | null;
  notes: string | null;
};

export type DpTerminationSummary = {
  terminationType: DpTerminationType;
  noticeType: DpNoticeType;
  separationDate: string;
  terminationDate: string;
  paymentDeadline: string;
  noticeDays: number | null;
  noticePaidDays: number | null;
  thirteenthAvos: number | null;
  thirteenthNoticeAvos: number | null;
  vacationDays: { overdue: number; doubled: number; proportional: number; notice: number };
  remainingTermDays: number | null;
  fgtsFinePct: number;
  fgtsFine: string | null;
};

/** Demonstrativo de rescisão de conferência (F2c), gravado ou a prévia calculada na hora (`saved: false`). */
export type DpTerminationPayment = {
  saved: boolean;
  /** null = prévia, ainda não gravada. */
  status: DpPayrollPeriodStatus | null;
  resultStatus: DpPayrollResultStatus;
  termination: {
    /** RESCISAO = desligamento concluído; PROCESSO = ainda em andamento (não fecha). */
    source: "RESCISAO" | "PROCESSO";
    processId: string | null;
    terminationType: DpTerminationType;
    noticeType: DpNoticeType;
    noticeStartDate: string | null;
    lastWorkingDay: string;
    terminationDate: string;
    separationDate: string;
  };
  paymentDate: string;
  /** Só na prévia: afastamento + 10 dias, no dia útil anterior. */
  suggestedPaymentDate?: string;
  /** Espelho de ponto do mês do afastamento fechado: a apuração manual não se aplica. */
  timesheetClosed: boolean;
  fgtsBalance: string | null;
  manualInput: DpTerminationManualInput | null;
  meta: DpPayrollResultMeta;
  summary: DpTerminationSummary;
  grossAmount: string;
  deductionsAmount: string;
  netAmount: string;
  inssBase: string;
  inssAmount: string;
  irrfBase: string;
  irrfAmount: string;
  fgtsBase: string;
  fgtsAmount: string;
  warnings: string[];
  /** Títulos do líquido e da multa do FGTS no Financeiro (ADR 0029); null na prévia. */
  financeiro: DpFinanceView | null;
  lines: Array<Omit<DpPayrollResultLine, "order">>;
  calculatedAt: string | null;
  closedAt: string | null;
  closedBy: { id: string; nomeCompleto: string } | null;
  reopenedAt: string | null;
  reopenedBy: { id: string; nomeCompleto: string } | null;
  reopenReason: string | null;
};

export type TerminationPaymentPayload = {
  paymentDate?: string;
  fgtsBalance?: number | null;
  manualInput?: PayrollMonthInputPayload | null;
};

const auth = (token: string) => {
  const raw = token?.startsWith("Bearer ") ? token.slice(7) : token;
  return { headers: { Authorization: `Bearer ${raw}` } };
};

async function call<T>(fallback: string, run: () => Promise<{ data: T }>): Promise<T> {
  try {
    const { data } = await run();
    return data;
  } catch (error) {
    throwApiError(error, fallback);
    throw error;
  }
}

const url = (contractId: string, suffix = "") => `/dp/contracts/${contractId}/termination-payment${suffix}`;

export const getTerminationPaymentApi = (token: string, contractId: string) =>
  call<DpTerminationPayment>("Erro ao carregar o demonstrativo de rescisão.", () => api.get(url(contractId), auth(token)));

export const updateTerminationPaymentApi = (token: string, contractId: string, payload: TerminationPaymentPayload) =>
  call<DpTerminationPayment>("Erro ao salvar o demonstrativo de rescisão.", () => api.patch(url(contractId), payload, auth(token)));

export const calculateTerminationPaymentApi = (token: string, contractId: string) =>
  call<DpTerminationPayment>("Erro ao calcular a rescisão.", () => api.post(url(contractId, "/calculate"), {}, auth(token)));

export const closeTerminationPaymentApi = (token: string, contractId: string) =>
  call<DpTerminationPayment>("Erro ao fechar a rescisão.", () => api.post(url(contractId, "/close"), {}, auth(token)));

export const reopenTerminationPaymentApi = (token: string, contractId: string, reason: string) =>
  call<DpTerminationPayment>("Erro ao reabrir a rescisão.", () => api.post(url(contractId, "/reopen"), { reason }, auth(token)));

export const getTerminationMonthContextApi = (token: string, contractId: string) =>
  call<DpPayrollMonthInputContext>("Erro ao carregar o que o sistema sabe do mês.", () =>
    api.get(url(contractId, "/month-context"), auth(token))
  );

/**
 * "Demonstrativo de rescisão — conferência" em PDF. Como no contracheque, o erro do
 * backend (404 não calculado, 409 pendente) chega como blob e é lido aqui.
 */
export const getTerminationPaymentPdfApi = async (token: string, contractId: string): Promise<Blob> => {
  try {
    const { data } = await api.get<Blob>(url(contractId, "/pdf"), { ...auth(token), responseType: "blob" });
    return data;
  } catch (error: any) {
    const body = error?.response?.data;
    if (body instanceof Blob) {
      let message = "Erro ao gerar o demonstrativo de rescisão.";
      try {
        const parsed = JSON.parse(await body.text());
        if (typeof parsed?.message === "string") message = parsed.message;
      } catch {
        // corpo não é JSON: fica a mensagem padrão
      }
      throw new Error(message);
    }
    throwApiError(error, "Erro ao gerar o demonstrativo de rescisão.");
    throw error;
  }
};

/** Selo do demonstrativo: prévia, aberto (pendente) ou fechado. */
export const terminationPaymentBadge = (payment: Pick<DpTerminationPayment, "saved" | "status" | "resultStatus">) =>
  !payment.saved
    ? { label: "Prévia", className: "dpBadge--gray" }
    : payment.status === "FECHADO"
      ? { label: "Fechado", className: "dpBadge--green" }
      : payment.resultStatus === "PENDENTE"
        ? { label: "Aberto · pendente", className: "dpBadge--orange" }
        : { label: "Aberto", className: "dpBadge--blue" };
